import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { LandingBlock, LandingBlockType } from '../../types';
import { SOCIAL_NETWORK_OPTIONS } from '../../data/socialNetworks';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { Button } from '../ui/Button';
interface Field {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'url' | 'toggle' | 'select';
  options?: {
    value: string;
    label: string;
  }[];
}
type ItemKind = 'benefit' | 'testimonial' | 'faq' | 'stat' | 'logo' | 'feature' | 'plan' | null;
const SCHEMA: Record<LandingBlockType, {
  fields: Field[];
  items: ItemKind;
}> = {
  topbar: {
    fields: [{
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }, {
      key: 'showLogo',
      label: 'Mostrar logo',
      kind: 'toggle'
    }, {
      key: 'showAuth',
      label: 'Mostrar botón «Iniciar sesión»',
      kind: 'toggle'
    }, {
      key: 'logoAlign',
      label: 'Alineación del logo',
      kind: 'select',
      options: [{
        value: 'left',
        label: 'A la izquierda'
      }, {
        value: 'center',
        label: 'Centrado'
      }]
    }, {
      key: 'logoHeight',
      label: 'Altura del logo (px)',
      kind: 'text'
    }],
    items: null
  },
  logo: {
    fields: [{
      key: 'logoHeight',
      label: 'Altura del logo (px)',
      kind: 'text'
    }],
    items: null
  },
  hero: {
    fields: [{
      key: 'eyebrow',
      label: 'Etiqueta superior',
      kind: 'text'
    }, {
      key: 'brandLine',
      label: 'Línea de marca (ATRAE · FILTRA · CONVIERTE)',
      kind: 'text'
    }, {
      key: 'title',
      label: 'Título',
      kind: 'textarea'
    }, {
      key: 'highlight',
      label: 'Palabras resaltadas en azul',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }, {
      key: 'subtitleHighlight',
      label: 'Palabras resaltadas del subtítulo',
      kind: 'text'
    }, {
      key: 'extraLine',
      label: 'Línea complementaria',
      kind: 'textarea'
    }, {
      key: 'showLogo',
      label: 'Mostrar logo sobre el título',
      kind: 'toggle'
    }, {
      key: 'logoHeight',
      label: 'Altura del logo (px)',
      kind: 'text'
    }],
    items: null
  },
  titulo: {
    fields: [{
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }],
    items: null
  },
  subtitulo: {
    fields: [{
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }],
    items: null
  },
  imagen: {
    fields: [{
      key: 'url',
      label: 'URL de la imagen',
      kind: 'url'
    }, {
      key: 'alt',
      label: 'Texto alternativo',
      kind: 'text'
    }],
    items: null
  },
  video: {
    fields: [{
      key: 'url',
      label: 'URL del vídeo (YouTube, Vimeo, Wistia o mp4)',
      kind: 'url'
    }, {
      key: 'thumbnailUrl',
      label: 'Miniatura',
      kind: 'url'
    }, {
      key: 'overlayTitle',
      label: 'Texto sobre la miniatura',
      kind: 'textarea'
    }, {
      key: 'overlayHighlight',
      label: 'Palabras resaltadas del texto superpuesto',
      kind: 'text'
    }, {
      key: 'note',
      label: 'Anotación manuscrita',
      kind: 'text'
    }, {
      key: 'caption',
      label: 'Texto bajo el vídeo',
      kind: 'textarea'
    }, {
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }],
    items: null
  },
  beneficios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'benefit'
  },
  estadisticas: {
    fields: [],
    items: 'stat'
  },
  testimonios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'testimonial'
  },
  logos_clientes: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }],
    items: 'logo'
  },
  formulario: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }, {
      key: 'showProgress',
      label: 'Mostrar barra de progreso',
      kind: 'toggle'
    }, {
      key: 'showPrivacy',
      label: 'Mostrar aviso de privacidad',
      kind: 'toggle'
    }],
    items: null
  },
  faq: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }],
    items: 'faq'
  },
  calendario: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }, {
      key: 'calendarSource',
      label: 'Calendario',
      kind: 'select',
      options: [{
        value: 'global',
        label: 'Google Calendar principal de la empresa'
      }, {
        value: 'custom',
        label: 'Calendario propio de esta landing'
      }]
    }, {
      key: 'url',
      label: 'URL de reserva propia (Calendly, Google Calendar o cualquier agenda)',
      kind: 'url'
    }, {
      key: 'calendarMode',
      label: 'Cómo se muestra',
      kind: 'select',
      options: [{
        value: 'boton',
        label: 'Botón que abre el calendario'
      }, {
        value: 'incrustado',
        label: 'Calendario incrustado en la página'
      }]
    }, {
      key: 'embedHeight',
      label: 'Altura del calendario incrustado (px)',
      kind: 'number'
    }, {
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }],
    items: null
  },
  whatsapp: {
    fields: [{
      key: 'number',
      label: 'Número de WhatsApp',
      kind: 'text'
    }, {
      key: 'message',
      label: 'Mensaje inicial',
      kind: 'text'
    }],
    items: null
  },
  cta_final: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }, {
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }, {
      key: 'whatsappNumber',
      label: 'Número de WhatsApp (si se rellena, el botón abre el chat)',
      kind: 'text'
    }, {
      key: 'whatsappMessage',
      label: 'Mensaje inicial de WhatsApp',
      kind: 'text'
    }],
    items: null
  },
  footer: {
    fields: [{
      key: 'text',
      label: 'Texto legal',
      kind: 'textarea'
    }],
    items: 'logo'
  },
  pilares: {
    fields: [],
    items: 'benefit'
  },
  frase: {
    fields: [{
      key: 'text',
      label: 'Frase destacada',
      kind: 'textarea'
    }],
    items: null
  },
  servicios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }],
    items: 'benefit'
  },
  caracteristicas: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'feature'
  },
  galeria: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'logo'
  },
  precios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }],
    items: 'plan'
  },
  contacto: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }, {
      key: 'email',
      label: 'Email',
      kind: 'text'
    }, {
      key: 'phone',
      label: 'Teléfono',
      kind: 'text'
    }, {
      key: 'address',
      label: 'Dirección',
      kind: 'text'
    }],
    items: null
  }
};
const ITEM_TEMPLATE: Record<string, Record<string, string>> = {
  benefit: {
    icon: 'Target',
    title: 'Nuevo beneficio',
    text: 'Describe el beneficio.'
  },
  testimonial: {
    name: '',
    role: '',
    company: '',
    result: '',
    text: '',
    avatar: ''
  },
  faq: {
    q: '',
    a: ''
  },
  stat: {
    value: '',
    label: ''
  },
  feature: {
    title: 'Nueva característica',
    text: 'Describe la característica.'
  },
  plan: {
    name: 'Nuevo plan',
    price: '0 €',
    period: 'al mes',
    features: 'Ventaja 1\nVentaja 2',
    featured: '',
    ctaLabel: 'Empezar'
  }
};
const ITEM_FIELDS: Record<string, {
  key: string;
  label: string;
}[]> = {
  benefit: [{
    key: 'icon',
    label: 'Icono'
  }, {
    key: 'title',
    label: 'Título'
  }, {
    key: 'text',
    label: 'Texto'
  }],
  testimonial: [{
    key: 'name',
    label: 'Nombre'
  }, {
    key: 'role',
    label: 'Cargo'
  }, {
    key: 'company',
    label: 'Empresa'
  }, {
    key: 'result',
    label: 'Resultado conseguido'
  }, {
    key: 'text',
    label: 'Testimonio'
  }, {
    key: 'avatar',
    label: 'Foto (URL)'
  }],
  faq: [{
    key: 'q',
    label: 'Pregunta'
  }, {
    key: 'a',
    label: 'Respuesta'
  }],
  stat: [{
    key: 'value',
    label: 'Cifra'
  }, {
    key: 'label',
    label: 'Etiqueta'
  }],
  feature: [{
    key: 'title',
    label: 'Título'
  }, {
    key: 'text',
    label: 'Descripción'
  }],
  plan: [{
    key: 'name',
    label: 'Nombre del plan'
  }, {
    key: 'price',
    label: 'Precio'
  }, {
    key: 'period',
    label: 'Periodo'
  }, {
    key: 'features',
    label: 'Ventajas (una por línea)'
  }, {
    key: 'featured',
    label: 'Destacado (escribe "sí")'
  }, {
    key: 'ctaLabel',
    label: 'Texto del botón'
  }]
};
const ICON_OPTIONS = ['Magnet', 'Filter', 'TrendingUp', 'Target', 'Users', 'Bot', 'Video', 'Megaphone', 'Sparkles', 'BarChart3', 'Building2', 'CalendarDays', 'MessageCircle', 'Briefcase'].map(value => ({
  value,
  label: value
}));

/** Campos de lista que se editan con área de texto. */
const MULTILINE_KEYS = ['text', 'a', 'features'];
interface BlockSettingsProps {
  block: LandingBlock;
  onChange: (block: LandingBlock) => void;
}
interface SocialLink {
  network: string;
  url: string;
}
const NETWORK_OPTIONS = SOCIAL_NETWORK_OPTIONS;

/**
 * Redes sociales del footer. Una red sin URL no se muestra en la landing, y si
 * ninguna tiene URL el bloque de iconos desaparece por completo.
 */
function SocialsEditor({
  block,
  onChange
}: BlockSettingsProps) {
  const socials = (block.config.socials ?? []) as SocialLink[];
  function update(next: SocialLink[]) {
    onChange({
      ...block,
      config: {
        ...block.config,
        socials: next
      }
    });
  }
  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= socials.length) return;
    const next = [...socials];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  }
  return <div className="space-y-2" data-id="element-290" data-mp="10yyac0g9huz7">
      <p className="text-sm font-medium text-foreground" data-id="element-291" data-mp="w27vfgg715fd">Redes sociales</p>
      <p className="text-xs text-muted-foreground" data-id="element-292" data-mp="w4ax4xqpkmzx">
        Deja la URL vacía para ocultar una red. Los enlaces se abren en una pestaña
        nueva.
      </p>
      {socials.map((social, index) => <div key={`social-${index}`} className="space-y-3 rounded-lg border border-border bg-surface p-3" data-id="element-293" data-mp="qv03z5eggol9" data-mpi={index}>
          <Select id={`${block.id}-social-${index}`} label="Red social" options={NETWORK_OPTIONS} value={social.network} onChange={e => {
        const next = [...socials];
        next[index] = {
          ...social,
          network: e.target.value
        };
        update(next);
      }} data-id="element-294" data-mp="vucldamk53gg" />
          <Input id={`${block.id}-social-url-${index}`} label="URL" type="url" placeholder="https://instagram.com/empresaoficial" value={social.url} onChange={e => {
        const next = [...socials];
        next[index] = {
          ...social,
          url: e.target.value
        };
        update(next);
      }} data-id="element-295" data-mp="19pf10ph7i76g" />
          <div className="flex items-center gap-1" data-id="element-296" data-mp="3lcv5ccmn5zlr">
            <Button variant="ghost" size="sm" aria-label="Subir" onClick={() => move(index, -1)} data-id="element-297" data-mp="3ldodni190x60">
              <ArrowUpIcon className="h-4 w-4" data-id="element-298" data-mp="3dcyv1cwkwdc8" />
            </Button>
            <Button variant="ghost" size="sm" aria-label="Bajar" onClick={() => move(index, 1)} data-id="element-299" data-mp="38e5kntcl92ga">
              <ArrowDownIcon className="h-4 w-4" data-id="element-300" data-mp="3nawihy6m663s" />
            </Button>
            <Button variant="ghost" size="sm" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-302" data-mp="3iaqpadkp4bjl" />} onClick={() => update(socials.filter((_, i) => i !== index))} data-id="element-301" data-mp="3ibjpswt2a4ta">
              Eliminar
            </Button>
          </div>
        </div>)}
      <Button variant="secondary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-304" data-mp="3ra5c931nm3zc" />} onClick={() => update([...socials, {
      network: 'instagram',
      url: ''
    }])} data-id="element-303" data-mp="3rczxgwshrstt">
        Añadir red social
      </Button>
    </div>;
}
export function BlockSettings({
  block,
  onChange
}: BlockSettingsProps) {
  const schema = SCHEMA[block.type];
  function setConfig(key: string, value: unknown) {
    onChange({
      ...block,
      config: {
        ...block.config,
        [key]: value
      }
    });
  }
  const listKey = block.type === 'footer' || block.type === 'logos_clientes' ? 'links' : 'items';
  const rawItems = (block.config[block.type === 'footer' ? 'links' : 'items'] ?? []) as unknown[];
  function setItems(next: unknown[]) {
    setConfig(block.type === 'footer' ? 'links' : 'items', next);
  }
  return <div className="space-y-4" data-id="element-305" data-mp="tg74lowqzcs3">
      {schema.fields.map(field => field.kind === 'toggle' ? <Switch key={field.key} id={`${block.id}-${field.key}`} label={field.label} checked={String(block.config[field.key] ?? 'si') !== 'no'} onChange={checked => setConfig(field.key, checked ? 'si' : 'no')} data-id="element-306" data-mp="erkrltulex41" /> : field.kind === 'select' ? <Select key={field.key} id={`${block.id}-${field.key}`} label={field.label} options={field.options ?? []} value={String(block.config[field.key] ?? field.options?.[0]?.value ?? '')} onChange={e => setConfig(field.key, e.target.value)} data-id="element-307" data-mp="5tsb4md4xjf2" /> : field.kind === 'textarea' ? <Textarea key={field.key} id={`${block.id}-${field.key}`} label={field.label} rows={3} value={String(block.config[field.key] ?? '')} onChange={e => setConfig(field.key, e.target.value)} data-id="element-308" data-mp="rt8xft4ssjp" /> : <Input key={field.key} id={`${block.id}-${field.key}`} label={field.label} type={field.kind === 'url' ? 'url' : 'text'} value={String(block.config[field.key] ?? '')} onChange={e => setConfig(field.key, e.target.value)} data-id="element-309" data-mp="z4fb56gh4nya" />)}

      <Select id={`${block.id}-spacing`} label="Espaciado" options={[{
      value: 'compacto',
      label: 'Compacto'
    }, {
      value: 'normal',
      label: 'Normal'
    }, {
      value: 'amplio',
      label: 'Amplio'
    }]} value={block.spacing} onChange={e => onChange({
      ...block,
      spacing: e.target.value as LandingBlock['spacing']
    })} data-id="element-310" data-mp="pechd41j35yw" />

      {/* Listas simples de texto (logos, enlaces del footer) */}
      {(schema.items === 'logo' || block.type === 'footer') && <div className="space-y-2" data-id="element-311" data-mp="25rsuokzs3j1s">
          <p className="text-sm font-medium text-foreground" data-id="element-312" data-mp="25qrl6kyev4m6">
            {block.type === 'footer' ? 'Enlaces legales' : block.type === 'galeria' ? 'URLs de las imágenes' : 'Nombres de empresa'}
          </p>
          {(rawItems as string[]).map((value, index) => <div key={`${listKey}-${index}`} className="flex gap-2" data-id="element-313" data-mp="2dbbyce0d4b2c" data-mpi={index}>
              <Input id={`${block.id}-item-${index}`} value={value} onChange={e => {
          const next = [...(rawItems as string[])];
          next[index] = e.target.value;
          setItems(next);
        }} data-id="element-314" data-mp="2i2tp698o8v9l" />
              <Button variant="ghost" size="sm" aria-label="Eliminar" onClick={() => setItems(rawItems.filter((_, i) => i !== index))} data-id="element-315" data-mp="1pfpqsn433f6s">
                <Trash2Icon className="h-4 w-4" data-id="element-316" data-mp="1ue0xioz319ks" />
              </Button>
            </div>)}
          <Button variant="secondary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-318" data-mp="23e7ckfafihm2" />} onClick={() => setItems([...(rawItems as string[]), ''])} data-id="element-317" data-mp="1zbapab9y1xbs">
            Añadir
          </Button>
        </div>}

      {/* Redes sociales del footer: URL, orden y visibilidad */}
      {block.type === 'footer' && <SocialsEditor block={block} onChange={onChange} data-id="element-319" data-mp="3es1c9loxvcfm" />}

      {/* Listas de objetos */}
      {schema.items && schema.items !== 'logo' && <div className="space-y-3" data-id="element-320" data-mp="1aprngcazswp3">
          <p className="text-sm font-medium text-foreground" data-id="element-321" data-mp="1aqknyvjcypys">Elementos</p>
          {(rawItems as Record<string, string>[]).map((item, index) => <div key={`item-${index}`} className="space-y-3 rounded-lg border border-border bg-surface p-3" data-id="element-322" data-mp="1oorajfiux3j5" data-mpi={index}>
              {ITEM_FIELDS[schema.items as string].map(field => field.key === 'icon' ? <Select key={field.key} id={`${block.id}-${index}-${field.key}`} label={field.label} options={ICON_OPTIONS} value={item[field.key] ?? ''} onChange={e => {
          const next = [...(rawItems as Record<string, string>[])];
          next[index] = {
            ...item,
            [field.key]: e.target.value
          };
          setItems(next);
        }} data-id="element-323" data-mp="w1od6qpx2yuh" /> : MULTILINE_KEYS.includes(field.key) ? <Textarea key={field.key} id={`${block.id}-${index}-${field.key}`} label={field.label} rows={3} value={item[field.key] ?? ''} onChange={e => {
          const next = [...(rawItems as Record<string, string>[])];
          next[index] = {
            ...item,
            [field.key]: e.target.value
          };
          setItems(next);
        }} data-id="element-324" data-mp="d8swex8tnrb2" /> : <Input key={field.key} id={`${block.id}-${index}-${field.key}`} label={field.label} value={item[field.key] ?? ''} onChange={e => {
          const next = [...(rawItems as Record<string, string>[])];
          next[index] = {
            ...item,
            [field.key]: e.target.value
          };
          setItems(next);
        }} data-id="element-325" data-mp="2ay7vr5mv7mzx" />)}
              <Button variant="ghost" size="sm" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-327" data-mp="2topj0uoxhqdw" />} onClick={() => setItems(rawItems.filter((_, i) => i !== index))} data-id="element-326" data-mp="37noz3dydkwyg">
                Eliminar elemento
              </Button>
            </div>)}
          <Button variant="secondary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-329" data-mp="1i8iselmonav5" />} onClick={() => setItems([...rawItems, {
        ...ITEM_TEMPLATE[schema.items as string]
      }])} data-id="element-328" data-mp="1i9adh2inlq76">
            Añadir elemento
          </Button>
        </div>}
    </div>;
}